/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { css } from "@emotion/react";
import { faFileArrowDown, faFileArrowUp, faMagnifyingGlass, faTrash } from "@fortawesome/pro-regular-svg-icons";
import {
    Badge,
    Row
} from "reactstrap";
import { deletePackingList, listPackingList, updateFilter } from "../../../actions/PackingList/actions";
import useConfig from "../../../actions/Tenants/config/configHook";
import { UserRole } from "../../../actions/Tenants/config/constantsTyped";
import { AvosAction } from "../../../actions/types";
import { useFields, usePagination } from "../../../hooks";
import { getFilledArrayOrDefault } from "../../../utils";
import useAuthorization from "../../../utils/authorization";
import SingleSelect from "../../Forms/SingleSelect";
import { Button } from "../../Helper/Button";
import { ConfirmationModal } from "../../Helper/Modal";
import { SearchInput } from "../../Helper/SearchInput";
import { getTableHead, Table, TableCell, TableHeaderCell } from "../../Helper/Table";
import { formatDateTime } from "../Layers/fields/formatters";
import FullPageLayout from "../Layout/FullPageLayout";
import PackingListReviewModal from "./PackingListReviewModal";
import PackingListUploadModal from "./PackingListUploadModal";
import TruncatedFileName from "./TruncatedFileName";

export function ListStatusBadge({ item }) {
    let color = "light";
    if (item.status === "SUCCESS") {
        color = "success";
    } else if (item.status === "FAILED") {
        color = "danger";
    } else if (item.status === "CREATED") {
        color = "warning";
    }
    return <Badge size="sm" color={color} className="w-100">{item.status}</Badge>;
}

// * Use this component to display packing list in standalone page
export default function PackingListPage() {
    return (
        <FullPageLayout title="Packing list import">
            <Row className="justify-content-center py-3 py-lg-5">
                <PackingListBody wrappedTable />
            </Row>
        </FullPageLayout>
    );
}

// * Use this component to display packing list in tab
export const PackingListTab = () => {
    return (
        <div className="pt-4">
            <PackingListBody />
        </div>
    );
};

export const PackingListBody = ({ wrappedTable }: { wrappedTable?: boolean; }) => {
    const dispatch = useDispatch();
    const items = useSelector((state: any) => state.packingLists.items);
    const isLoading = useSelector((state: any) => state.packingLists.isLoading);
    const [searchParams, setSearchParams] = useSearchParams();
    const [importModal, setImportModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState<string|false>(false);
    const user = useSelector((state: any) => state.auth.user);
    const auth = useAuthorization();
    const { applyDisplayFilterLayerFields } = useFields();

    // Get list_id from URL params
    const previewModal = searchParams.get("list_id");
    const supplier = searchParams.get("supplier");

    const filter = useSelector((state: any) => state.packingLists.filter);
    const config = useConfig();
    const supplierOptions = getFilledArrayOrDefault(config.form_options?.supplier);

    const { offset, limit } = usePagination();

    useEffect(() => {
        if (!isLoading) {
            dispatch(listPackingList(filter) as any);
        }
    }, [filter]);

    useEffect(() => {
        if (filter.offset !== offset || filter.limit !== limit) {
            dispatch(updateFilter({ ...filter, offset, limit }));
        }
    }, [offset, limit]);

    useEffect(() => {
        if (supplier !== (filter.supplier || null)) {
            dispatch(updateFilter({ ...filter, supplier: supplier || undefined }));
        }
    }, [supplier]);

    const download = (item) => {
        window.location.href = item.url;
    };

    const onDelete = (id: string, confirmed?: boolean) => {
        if (confirmed) {
            dispatch(deletePackingList(id) as AvosAction);
            setDeleteModal(false);
            setSearchParams((prev) => {
                prev.delete("list_id");
                return prev;
            });
            return;
        }

        setDeleteModal(id);
    };

    const onSearch = (value?: string) => {
        dispatch(listPackingList({ ...filter, offset, limit, search: value }) as AvosAction);
    };

    const fields = useMemo(() => {
        const default_fields = [
            {
                field: {
                    field_id: "LABEL_LINK",
                    fieldname_getter: "label",
                    label: "Label",
                    value_if_null: "-",
                },
                display_filter: false
            },
            {
                field: {
                    field_id: "CUSTOM_LAYER_META",
                    fieldname_getter: "container_no",
                    label: "Container",
                    value_if_null: "-",
                },
                display_filter: false
            },
            {
                field: {
                    field_id: "CUSTOM_LAYER_META",
                    fieldname_getter: "country",
                    label: "Country",
                    value_if_null: "-",
                },
                display_filter: false
            },
            {
                field: {
                    field_id: "CUSTOM_LAYER_META",
                    fieldname_getter: "supplier",
                    label: "Supplier",
                    value_if_null: "-",
                },
                display_filter: false
            },
            {
                field: {
                    field_id: "CUSTOM_LAYER_META",
                    fieldname_getter: "fruit_type",
                    label: "Fruit type",
                    value_if_null: "-",
                },
                display_filter: false
            }
        ];

        const packing_list_table_fields = getFilledArrayOrDefault(config.root_config.packing_list_table_fields, default_fields);

        return applyDisplayFilterLayerFields(packing_list_table_fields, {}); // No filters yet, but could be per fruit type at some point
    }, [config.root_config.packing_list_table_fields]);

    const header = useMemo(() => (
        <tr className="position-relative">
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>File name</TableHeaderCell>

            {getTableHead(fields)}

            <TableHeaderCell>Created</TableHeaderCell>
            <TableHeaderCell sticky />
        </tr>
    ), [fields.length]);

    return (
        <div className="w-100 d-flex flex-column gap-3">
            <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <div css={css`
                        width: 18rem;
                        @media (max-width: 576px) {
                            width: 100%;
                        }
                    `}>
                        <SearchInput placeholder="Search packing list" onSearch={onSearch} maxWidth="100%" />
                    </div>
                    <div css={css`
                        width: 18rem;
                        @media (max-width: 576px) {
                            width: 100%;
                        }
                    `}>
                        <SingleSelect
                            options={supplierOptions.map(({ value, label }) => ({ value, label }))}
                            field="supplier"
                            setValue={(_, value) => setSearchParams((prev) => {
                                if (value) {
                                    prev.set("supplier", value as string);
                                } else {
                                    prev.delete("supplier");
                                }
                                return prev;
                            })}
                            post_value={supplier as string}
                            label={false}
                            multi={false}
                            isClearable={true}
                            return_array={false}
                            placeholder="Filter by supplier"
                            unwrapped
                        />
                    </div>
                </div>

                <Button
                    className="text-nowrap"
                    color="primary"
                    onClick={() => setImportModal(true)}
                    css={css`width: fit-content;`}
                    icon={faFileArrowUp}
                >
                    Import packing list
                </Button>
            </div>

            <Table
                header={header}
                isLoading={isLoading}
                withWrapper={wrappedTable}
                count={items.count}
                emptyStateText="Upload a packing list to get started"
                emptyStateActions={<Button
                    color="primary"
                    onClick={() => setImportModal(true)}
                    icon={faFileArrowUp}
                >
                    Import packing list
                </Button>}
            >
                <tbody>
                    {getFilledArrayOrDefault(items?.results).map((item, index) => (
                        <tr key={index} title={item.id} className="position-relative">
                            <TableCell><ListStatusBadge item={item}></ListStatusBadge></TableCell>
                            <TableCell width="100%"><TruncatedFileName name={item.file_name}></TruncatedFileName></TableCell>

                            {fields.map((field, fieldIndex) => (
                                <TableCell key={fieldIndex}>
                                    {field.set_value({
                                        config,
                                        layer: item.layer ?? item,
                                        check: null, // * To be used in check tables and layer overview
                                        fruit: null, // * Fruit list is not available on overview screen
                                        children: null, // * children is only available on layer index page
                                        form_options: [] // * used to translate form values to labels
                                    }).display_component()}
                                </TableCell>
                            ))}

                            <TableCell>{formatDateTime(item.created)}</TableCell>

                            <TableCell sticky>
                                <Button iconOnlyBreakpoint="md" size="sm" className="text-nowrap" color="light" icon={faMagnifyingGlass} onClick={() => setSearchParams({ list_id: item.id })}>View</Button>
                                <Button iconOnlyBreakpoint="md" size="sm" className="text-nowrap" color="light" icon={faFileArrowDown} onClick={() => download(item)}>Download</Button>
                                {(user.is_admin || auth.userHasRole(UserRole.TENANT_ADMIN)) && (
                                    <Button iconOnlyBreakpoint="md" size="sm" className="text-nowrap" color="danger" icon={faTrash} onClick={() => onDelete(item.id)}></Button>
                                )}
                            </TableCell>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <PackingListUploadModal
                isOpen={importModal}
                toggle={() => setImportModal(!importModal)}
            />

            <PackingListReviewModal
                list_id={previewModal || false}
                isOpen={previewModal !== null}
                toggle={() => setSearchParams({})}
                onDelete={onDelete}
                download={(list_id: string) => download(items.results.find((item: any) => item.id === list_id))}
            />

            <ConfirmationModal
                header="Delete packing list"
                isOpen={!!deleteModal}
                onClose={() => setDeleteModal(false)}
                onConfirm={() => onDelete(deleteModal as string, true)}
                size="m"
                color="danger"
            >
                Are you sure you want to delete this packing list?
            </ConfirmationModal>
        </div>
    );
};
